import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _createSuper from "@babel/runtime/helpers/esm/createSuper";

var _dec, _dec2, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { testable } from '@instructure/ui-testable';
import { themeable } from '@instructure/ui-themeable';
import { omitProps, getElementType } from '@instructure/ui-react-utils';
import { Tooltip } from '@instructure/ui-tooltip';
import { hasVisibleChildren } from '@instructure/ui-a11y-utils';
var styles = {
  componentId: 'lQGQR',
  template: function template(theme) {
    return "\n\n.lQGQR_bGBk{-ms-hyphens:auto;-webkit-hyphens:auto;-webkit-text-decoration:".concat(theme.linkTextDecoration || 'inherit', ";background-color:").concat(theme.backgroundColor || 'inherit', ";border:none;box-sizing:border-box;color:").concat(theme.fontColor || 'inherit', ";cursor:pointer;display:block;font-family:").concat(theme.fontFamily || 'inherit', ";font-weight:").concat(theme.fontWeight || 'inherit', ";hyphens:auto;overflow-wrap:break-word;padding:").concat(theme.contentPadding || 'inherit', ";text-align:center;text-decoration:").concat(theme.linkTextDecoration || 'inherit', ";width:100%;word-wrap:break-word}\n\n[dir=ltr] .lQGQR_bGBk,[dir=rtl] .lQGQR_bGBk{text-align:center}\n\n.lQGQR_bGBk:active,.lQGQR_bGBk:focus,.lQGQR_bGBk:hover{background-color:").concat(theme.hoverBackgroundColor || 'inherit', "}\n\n.lQGQR_bGBk:focus{box-shadow:").concat(theme.outerFocusOutline || 'inherit', ",").concat(theme.innerFocusOutline || 'inherit', ";outline:none}\n\n.lQGQR_dnnz{fill:").concat(theme.iconColor || 'inherit', ";font-size:").concat(theme.iconSize || 'inherit', "}\n\n.lQGQR_blJt{font-size:").concat(theme.fontSize || 'inherit', ";line-height:").concat(theme.lineHeight || 'inherit', "}\n\n.lQGQR_fGhm{color:").concat(theme.selectedFontColor || 'inherit', ";fill:").concat(theme.selectedIconColor || 'inherit', "}\n\n.lQGQR_fGhm,.lQGQR_fGhm:active,.lQGQR_fGhm:focus,.lQGQR_fGhm:hover{background-color:").concat(theme.selectedBackgroundColor || 'inherit', "}\n\n.lQGQR_fGhm:focus{box-shadow:").concat(theme.selectedOuterFocusOutline || 'inherit', ",").concat(theme.selectedInnerFocusOutline || 'inherit', ";outline:none}");
  },
  'root': 'lQGQR_bGBk',
  'icon': 'lQGQR_dnnz',
  'label': 'lQGQR_blJt',
  'selected': 'lQGQR_fGhm'
};
import theme from "./theme.js";
/**
---
parent: Navigation
id: Navigation.Item
---
**/

var NavigationItem = (_dec = testable(), _dec2 = themeable(theme, styles), _dec(_class = _dec2(_class = (_temp = _class2 = /*#__PURE__*/function (_Component) {
  _inherits(NavigationItem, _Component);

  var _super = _createSuper(NavigationItem);

  function NavigationItem() {
    _classCallCheck(this, NavigationItem);

    return _super.apply(this, arguments);
  }

  _createClass(NavigationItem, [{
    key: "renderLink",
    value: function renderLink() {
      var _classnames;

      var ElementType = getElementType(NavigationItem, this.props);
      var _this$props = this.props,
          href = _this$props.href,
          onClick = _this$props.onClick,
          selected = _this$props.selected,
          icon = _this$props.icon,
          label = _this$props.label;
      var classes = classnames((_classnames = {}, _defineProperty(_classnames, styles.root, true), _defineProperty(_classnames, styles.selected, selected), _classnames));
      var props = omitProps(this.props, NavigationItem.propTypes);
      return /*#__PURE__*/React.createElement(ElementType, Object.assign({}, props, {
        href: href,
        onClick: onClick,
        className: classes,
        "aria-label": this.props.minimized ? label : null
      }), /*#__PURE__*/React.createElement("div", {
        className: styles.icon,
        "aria-hidden": "true"
      }, icon), !this.props.minimized ? /*#__PURE__*/React.createElement("div", {
        className: styles.label
      }, label) : null);
    }
  }, {
    key: "render",
    value: function render() {
      var _this$props2 = this.props,
          minimized = _this$props2.minimized,
          label = _this$props2.label;
      var link = this.renderLink();
      return minimized && hasVisibleChildren(label) ? /*#__PURE__*/React.createElement(Tooltip, {
        renderTip: label,
        placement: "end"
      }, link) : link;
    }
  }]);

  NavigationItem.displayName = "NavigationItem";
  return NavigationItem;
}(Component), _class2.propTypes = {
  /**
   * The visual to display (ex. an Image, Logo, Avatar, or Icon)
   */
  icon: PropTypes.node.isRequired,

  /**
   * The text to display  for the Navigation Link
   */
  label: PropTypes.node.isRequired,

  /**
   * The element type to render as (will default to `<a>` if href is provided)
   */
  as: PropTypes.elementType,

  /**
   * If the NavigationItem goes to a new page, pass an href
   */
  href: PropTypes.string,

  /**
   * If the NavigationItem does not go to a new page pass an onClick
   */
  onClick: PropTypes.func,

  /**
   * Denotes which NavigationItem is currently selected
   */
  selected: PropTypes.bool,

  /**
   * When minimized is set to true, the `<Navigation />` shows icons only while the text becomes a tooltip. When it is set to false, the `<Navigation />` shows text in addition to the icons
   */
  minimized: PropTypes.bool
}, _class2.defaultProps = {
  as: 'a',
  onClick: function onClick(e, selected) {},
  selected: false,
  minimized: false,
  href: void 0
}, _temp)) || _class) || _class);
export default NavigationItem;
export { NavigationItem };